import { useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import ListItemIcon from "@mui/material/ListItemIcon";
import LogoutIcon from "@mui/icons-material/Logout";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import PublicIcon from "@mui/icons-material/Public";
import EditLocationAltIcon from "@mui/icons-material/EditLocationAlt";
import HomeIcon from "@mui/icons-material/Home";
import { useAlert } from "../hook/useAlert";
import { useUser } from "../context/UserContext";
import { updateUser } from "../api/User";
import RegionSelectionModal from "./RegionSelectionModal";
import TCCCLogo from "../assets/images/tccc-logo-black.png";

const REGION_CHANGE_ROLES = ["ops", "admin"];

const Navbar = () => {
  const { user, refetchUser } = useUser();
  const { instance } = useMsal();
  const { onError, onSuccess } = useAlert();
  const [anchorUserEl, setAnchorUserEl] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [regionModalOpen, setRegionModalOpen] = useState(false);
  const [regionLoading, setRegionLoading] = useState(false);
  const [regionError, setRegionError] = useState("");
  const userMenuOpen = Boolean(anchorUserEl);
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();

  const canChangeRegion = REGION_CHANGE_ROLES.includes(user?.role);

  const handleOnClickUserMenu = (event) => {
    setAnchorUserEl(event.currentTarget);
  };

  const handleOnCloseUserMenu = () => {
    setAnchorUserEl(null);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("accessToken");
    instance.logoutRedirect().catch((error) => {
      onError(error);
    });
  };

  const handleOpenRegionModal = () => {
    setRegionError("");
    setRegionModalOpen(true);
    handleOnCloseUserMenu();
  };

  const handleRegionChange = async (region) => {
    setRegionLoading(true);
    setRegionError("");
    try {
      await updateUser(region);
      await refetchUser();
      setRegionModalOpen(false);
      onSuccess("Region updated successfully.");
    } catch (error) {
      const message = error?.message || "Failed to update region. Please try again.";
      setRegionError(message);
      onError(message);
    } finally {
      setRegionLoading(false);
    }
  };

  const getUserInitials = () => {
    if (!authUser?.name) return "U";
    const nameParts = authUser.name.split(" ");
    if (nameParts.length > 1) {
      return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`;
    }
    return authUser.name[0];
  };

  const getRegionDisplayName = (region) => {
    const regionMap = {
      EUOU: "Europe",
      EMEOU: "Eurasia Middle East",
      AOU: "Africa",
      NAOU: "North America",
      LATAM: "Latin America",
      APAC: "Asia Pacific",
    };
    return regionMap[region] || region;
  };

  useEffect(() => {
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      setAuthUser(accounts[0]);
    }
  }, [instance]);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "white",
          color: "black",
        }}
        className="animate__animated animate__fadeIn animate__delay-05s"
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center">
            <Link
              variant="h6"
              fontWeight={500}
              href="/app"
              underline="none"
              color="inherit"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <img alt="tccc logo" src={TCCCLogo} width={125} height="auto" />
            </Link>
          </Box>
          {isAuthenticated && (
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={2}>
              <HomeIcon onClick={() => navigate("/app")} sx={{ cursor: "pointer" }} />
              {user?.region && (
                <Chip
                  icon={<PublicIcon />}
                  label={user.region}
                  size="small"
                  variant="outlined"
                  sx={{
                    color: "black",
                    borderColor: "rgba(8, 8, 8, 0.5)",
                    "& .MuiChip-icon": { color: "black" },
                  }}
                />
              )}
              <IconButton
                size="small"
                aria-label="user account"
                aria-controls={userMenuOpen ? "user-menu" : undefined}
                aria-expanded={userMenuOpen ? "true" : undefined}
                onClick={handleOnClickUserMenu}
                color="inherit"
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    color: "white",
                    fontSize: "13px",
                    fontWeight: "bold",
                  }}
                >
                  {getUserInitials()}
                </Avatar>
              </IconButton>
              <Menu
                id="user-menu"
                anchorEl={anchorUserEl}
                open={userMenuOpen}
                onClose={handleOnCloseUserMenu}
                onClick={handleOnCloseUserMenu}
                slotProps={{
                  paper: {
                    sx: {
                      filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                      minWidth: 220,
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    {authUser?.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {authUser?.username}
                  </Typography>
                  {user?.region && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                      <PublicIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                        {getRegionDisplayName(user.region)}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Divider />
                {canChangeRegion && (
                  <MenuItem onClick={handleOpenRegionModal} sx={{ py: 1.5 }}>
                    <ListItemIcon>
                      <EditLocationAltIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Change Region
                    </Typography>
                  </MenuItem>
                )}

                <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Sign out
                  </Typography>
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />
      {canChangeRegion && (
        <RegionSelectionModal
          open={regionModalOpen}
          onRegionSelect={handleRegionChange}
          loading={regionLoading}
          error={regionError}
          onClose={() => setRegionModalOpen(false)}
          isUpdate
        />
      )}
    </>
  );
};

export default Navbar;
